import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowUpRight, Link2 } from "lucide-react";
import { motion } from "framer-motion";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export default function PublicPage() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    if (!handle) { setError("no_handle"); return; }
    (async () => {
      try {
        const r = await api.get(`/public/${handle}`);
        setData(r.data);
      } catch { setError("not_found"); }
    })();
  }, [handle]);

  // Dynamic SEO metadata
  useEffect(() => {
    if (!data) return;
    const { profile, categories = [] } = data;
    const topCats = categories.slice(0, 3).map((c) => c.name).join(", ");
    const title = profile.seo_title
      ? profile.seo_title
      : (topCats ? `${profile.name} | ${topCats}` : `${profile.name} — Curator Hub`);
    const description = profile.seo_description || profile.bio || `Curated links from ${profile.name}.`;
    const canonical = `${window.location.origin}/u/${profile.handle}`;

    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonical, "property");
    if (profile.picture) setMeta("og:image", profile.picture, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (profile.picture) setMeta("twitter:image", profile.picture);
    setCanonical(canonical);

    // JSON-LD structured data
    const existing = document.getElementById("public-page-jsonld");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "public-page-jsonld";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      mainEntity: {
        "@type": "Organization",
        name: profile.name,
        description,
        url: canonical,
        image: profile.picture || undefined,
      },
    });
    document.head.appendChild(script);
  }, [data]);

  if (error === "no_handle") {
    return (
      <div className="grain-overlay dark-select min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-8">
        <div className="max-w-lg text-center">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">Curator Hub</div>
          <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tighter">The stage is empty.</h1>
          <p className="text-zinc-400 mt-6">Sign in to claim your handle and start publishing.</p>
          <a href="/login" data-testid="empty-login-link" className="inline-block mt-8 border border-white/20 hover:bg-white hover:text-zinc-950 transition-colors px-6 py-3 text-sm tracking-[0.15em] uppercase">Enter admin</a>
        </div>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <div className="text-center">
          <div className="font-heading text-2xl">Profile not found</div>
          <div className="text-zinc-500 text-sm mt-2">The handle @{handle} doesn't exist.</div>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-zinc-950 text-zinc-500 flex items-center justify-center text-sm">Loading…</div>;

  const { profile, categories, links } = data;
  const theme = profile.theme === "light" ? "light" : "dark";
  const accent = profile.accent_color || "#d4af37";
  const cats = categories || [];
  const filtered = activeCat === "all" ? links : links.filter((l) => l.category_id === activeCat);
  const grouped = cats.map((c) => ({ cat: c, items: links.filter((l) => l.category_id === c.id) }));
  const uncategorized = links.filter((l) => !l.category_id);

  const isDark = theme === "dark";
  const rootClasses = isDark
    ? "grain-overlay dark-select bg-zinc-950 text-zinc-100"
    : "bg-stone-50 text-zinc-950";
  const cardClasses = isDark
    ? "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
    : "border-zinc-200 bg-white hover:bg-zinc-50";
  const secondaryText = isDark ? "text-zinc-400" : "text-zinc-600";
  const mutedText = isDark ? "text-zinc-500" : "text-zinc-500";
  const hostnameText = isDark ? "text-zinc-600" : "text-zinc-400";
  const filterActive = isDark ? "bg-white text-zinc-950 border-white" : "bg-zinc-950 text-white border-zinc-950";
  const filterIdle = isDark ? "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-950 hover:text-zinc-950";
  const emptyBorder = isDark ? "border-white/10" : "border-zinc-200";

  return (
    <div
      className={`min-h-screen font-body relative ${rootClasses}`}
      style={{ "--accent": accent }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[500px] pointer-events-none"
        style={{ background: `linear-gradient(to bottom, ${accent}0F, transparent)` }}
      />

      <header className="relative z-10 max-w-3xl mx-auto px-6 pt-24 pb-14">
        <div className="flex flex-col items-center text-center">
          {profile.picture ? (
            <img src={profile.picture} alt={profile.name} className={`w-24 h-24 rounded-full object-cover border ${isDark ? "border-white/10" : "border-zinc-200"} shadow-[0_8px_32px_rgba(0,0,0,0.15)]`} />
          ) : (
            <div className={`w-24 h-24 rounded-full border ${isDark ? "border-white/10" : "border-zinc-200"} flex items-center justify-center font-heading text-2xl`}>{profile.name?.[0] || "•"}</div>
          )}
          <div className={`mt-6 text-xs tracking-[0.3em] uppercase ${mutedText}`}>Curated Directory</div>
          <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{profile.name || "Curator"}</h1>
          {profile.bio ? <p className={`${secondaryText} mt-6 max-w-md leading-relaxed`}>{profile.bio}</p> : null}
        </div>

        {/* Affiliate disclosure - required near top, FTC-compliant */}
        <div
          data-testid="affiliate-disclosure-top"
          role="note"
          aria-label="Affiliate disclosure"
          className={`mt-10 mx-auto max-w-2xl border-l-2 py-3 px-5 text-sm leading-relaxed ${
            isDark
              ? "border-l-[color:var(--accent)] bg-white/[0.03] text-zinc-300"
              : "border-l-[color:var(--accent)] bg-white text-zinc-700"
          }`}
        >
          <span className={`font-heading tracking-tight ${isDark ? "text-white" : "text-zinc-950"}`}>Disclosure:</span>{" "}
          I may earn a commission when you purchase through links on this site, at no extra cost to you.
        </div>

        {cats.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mt-10">
            <button onClick={() => setActiveCat("all")} data-testid="filter-all" className={`text-xs tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${activeCat === "all" ? filterActive : filterIdle}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} data-testid={`filter-${c.id}`} onClick={() => setActiveCat(c.id)}
                className={`text-xs tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${activeCat === c.id ? filterActive : filterIdle}`}
                style={activeCat === c.id ? {} : { borderLeftColor: c.color, borderLeftWidth: 2 }}>
                {c.name}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 pb-32 space-y-16">
        {links.length > 0 && (
          <div
            data-testid="affiliate-disclosure-links"
            className={`text-xs tracking-[0.15em] uppercase text-center ${mutedText} -mb-8`}
          >
            Affiliate links · I may earn a commission at no extra cost to you
          </div>
        )}
        {links.length === 0 ? (
          <div className={`text-center py-24 border border-dashed ${emptyBorder}`}>
            <Link2 className={`w-8 h-8 mx-auto ${isDark ? "text-zinc-700" : "text-zinc-300"}`} />
            <div className={`mt-4 ${mutedText} text-sm`}>No public links yet.</div>
          </div>
        ) : activeCat === "all" ? (
          <>
            {grouped.filter(g => g.items.length > 0).map((g, gi) => (
              <section key={g.cat.id}>
                <div className="flex items-baseline justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ background: g.cat.color }} />
                    <h2 className="font-heading text-2xl tracking-tight">{g.cat.name}</h2>
                  </div>
                  <span className={`font-mono text-xs ${hostnameText}`}>{String(g.items.length).padStart(2, "0")}</span>
                </div>
                <div className="space-y-3">
                  {g.items.map((l, i) => <LinkCard key={l.id} link={l} index={gi * 10 + i} isDark={isDark} accent={accent} cardClasses={cardClasses} secondaryText={secondaryText} hostnameText={hostnameText} />)}
                </div>
              </section>
            ))}
            {uncategorized.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl tracking-tight mb-6">More</h2>
                <div className="space-y-3">
                  {uncategorized.map((l, i) => <LinkCard key={l.id} link={l} index={i} isDark={isDark} accent={accent} cardClasses={cardClasses} secondaryText={secondaryText} hostnameText={hostnameText} />)}
                </div>
              </section>
            )}
          </>
        ) : (
          <div className="space-y-3">
            {filtered.map((l, i) => <LinkCard key={l.id} link={l} index={i} isDark={isDark} accent={accent} cardClasses={cardClasses} secondaryText={secondaryText} hostnameText={hostnameText} />)}
          </div>
        )}
      </main>

      <footer className={`relative z-10 text-center pb-10 text-xs tracking-[0.2em] uppercase ${isDark ? "text-zinc-700" : "text-zinc-400"}`}>
        Curated with <span style={{ color: accent }}>Curator Hub</span>
      </footer>
    </div>
  );
}

function LinkCard({ link, index, isDark, accent, cardClasses, secondaryText, hostnameText }) {
  const image = link.og_image;
  const hostname = (() => { try { return new URL(link.url).hostname.replace("www.", ""); } catch { return link.url; } })();

  return (
    <motion.a
      href={`${API}/go/${link.id}`}
      target="_blank"
      rel="noopener noreferrer sponsored"
      data-testid={`public-link-${link.id}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index, 10) * 0.05 }}
      className={`group block relative border ${cardClasses} overflow-hidden transition-all duration-200`}
    >
      {image ? (
        <div className="aspect-[16/7] w-full overflow-hidden relative">
          <img src={image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className={`absolute inset-0 ${isDark ? "bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" : "bg-gradient-to-t from-white via-white/40 to-transparent"}`} />
        </div>
      ) : null}
      <div className={`flex items-start justify-between gap-6 p-6 sm:p-7 ${image ? "-mt-16 relative z-10" : ""}`}>
        <div className="min-w-0 flex-1">
          <div className={`font-heading text-lg sm:text-xl tracking-tight ${isDark ? "text-white" : "text-zinc-950"} group-hover:translate-x-1 transition-transform duration-200`}>
            {link.title || link.url}
          </div>
          {link.description ? <div className={`text-sm ${secondaryText} mt-2 leading-relaxed line-clamp-2`}>{link.description}</div> : null}
          <div className={`mt-3 font-mono text-[10px] tracking-wider ${hostnameText} truncate`}>{hostname}</div>
        </div>
        <ArrowUpRight className={`w-5 h-5 ${hostnameText} group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-200 shrink-0`} style={{ transitionProperty: "transform, color" }} />
      </div>
    </motion.a>
  );
}
